<template>
  <div
    class="custom-player-container"
    :class="{
      'custom-player-container__electron': isElectron,
      'custom-player-container__h5': isH5,
      'custom-player-container__landscape': isLandscape,
      'custom-player-container__component': isComponent,
    }"
  >
    <div
      v-show="!showNetworkError"
      class="dplayer-wrapper"
      :class="{
        'dplayer-wrapper__full': fullScreenStatus && (isH5 || openAiMode),
        'dplayer-hide-controller ': !isShowControls,
        'dplayer-mobile dplayer ': isH5,
      }"
      ref="dplayerContainer"
    ></div>
    <div
      v-if="isAudioLesson"
      class="audio-cover-wrap"
      :class="{ 'is-visualizing': audioVisualizerActive }"
    >
      <img v-if="coverUrl" class="audio-cover" :src="coverUrl" alt="" />
      <div class="audio-cover-template" aria-hidden="true">
        <div class="audio-visual-core">
          <div class="audio-pulse-ring"></div>
          <div class="audio-wave-bars">
            <span
              v-for="bar in 15"
              :key="bar"
              :style="{ animationDelay: `${bar * -0.08}s` }"
            ></span>
          </div>
        </div>
      </div>
    </div>
    <!-- 顶部渐变遮罩 -->
    <div
      class="top-gradient-overlay"
      :class="{
        'top-gradient-overlay__full': fullScreenStatus && (isH5 || openAiMode),
        'top-gradient-overlay__show': isShowControls && !isScreenshot,
      }"
    >
      <h2 class="top-gradient-title">{{ title || "" }}</h2>
    </div>
    <!-- 字幕覆盖层 -->
    <SubtitleOverlay
      ref="subtitleOverlay"
      :subtitleUrl="subtitleUrl"
      :currentTime="currentTime"
      :isFullScreen="fullScreenStatus"
      :controlsVisible="isShowControls"
      @subtitleToggle="subtitleActive = $event"
    />
    <!-- 顶部 -->
    <template v-if="isShowTop">
      <div
        v-if="isElectron"
        class="electron-play-header"
        :class="{
          'play-header__full': fullScreenStatus && (isH5 || openAiMode),
          'play-header__show': isShowControls && !isScreenshot,
        }"
        @dblclick.self="handleDbclick"
      >
        <!-- 普通看课模式 -->
        <template v-if="!openAiMode">
          <ToolBar
            v-if="!fullScreenStatus"
            class="tool-bar"
            :beforeClose="closeWin"
          />
          <div class="name">
            {{ title || "" }}
          </div>
        </template>
        <div
          class="operate-wrap"
          :class="{ 'is-win': isWin && !fullScreenStatus && !openAiMode }"
        >
          <button v-if="!fullScreenStatus" class="top-btn">
            <img @click="handleOnTop" :src="topIcon" alt="" />
          </button>
          <button @click="networkTest" class="network-test-btn">
            <img
              src="../../../../../assets/images/reply/network-test.png"
              alt=""
            />
          </button>
        </div>
      </div>
      <div
        v-else-if="!fullScreenStatus"
        class="play-header"
        :class="{
          'play-header__full': fullScreenStatus,
          'play-header__show': isShowControls && !isScreenshot,
        }"
      >
        <div class="back-wrap">
          <div class="back-btn" v-if="showBack" @click="$emit('back')">
            <img src="../../../../../assets/images/reply/back.png" alt="" />
          </div>
        </div>
        <div class="name">
          {{ title || "" }}
        </div>
      </div>
    </template>

    <div
      v-if="!isH5"
      class="paly-pause-icon-box"
      :class="{
        'paly-pause-icon-box__show': isShowPlayPauseIcon,
      }"
    >
      <img :src="videoPlayPauseIcon" alt="" />
    </div>
    <!-- 居中大播放按钮（暂停态） -->
    <div
      v-if="!isPlaying && !isH5"
      class="big-play-btn"
      @click="togglePlayPause"
    >
      <svg width="36" height="36" viewBox="0 0 24 24" fill="white">
        <polygon points="5,3 19,12 5,21" />
      </svg>
    </div>
    <!-- logo -->
    <div
      v-if="orgName"
      class="logo-container"
      :class="{
        'logo-container__full': fullScreenStatus && (isH5 || openAiMode),
      }"
    >
      <img class="logo" :src="faviconUrl" alt="" />
      <div class="divider"></div>
      <div class="org-name">{{ orgName }}</div>
    </div>
    <!-- notice -->
    <div
      class="notice-box"
      :class="{
        'notice-box__full': fullScreenStatus,
      }"
      v-if="notice"
    >
      {{ notice }}
    </div>
    <!-- 控制区 -->
    <!-- 续播提示 toast -->
    <div
      class="last-play-position"
      :class="{
        'last-play-position__full': fullScreenStatus && (isH5 || openAiMode),
        'last-play-position__show': isShowControls && !isScreenshot,
      }"
      v-if="startTime && isShowReply"
    >
      <span>已自动定位至上次观看位置</span>
      <div class="reply-btn" @click="handleReply">从头学</div>
    </div>
    <!-- 控制区（两行布局） -->
    <div
      class="player-controls"
      :class="{
        'player-controls__full': fullScreenStatus && (isH5 || openAiMode),
        'player-controls__show': isShowControls && !isScreenshot,
      }"
    >
      <!-- 第一行：进度条 -->
      <div class="progress-row">
        <span class="current-time">{{ formatTime(currentTime) }}</span>
        <div class="progress-bar-wrap">
          <DraggableProgressBar
            ref="progressBar"
            class="progress-bar"
            :value="currentProgress"
            :initCurrentTime="currentTime"
            :initDuration="duration"
            :isLandscape="isLandscape"
            @change="seekTo"
          ></DraggableProgressBar>
        </div>
        <span class="total-time">{{ formatTime(duration) }}</span>
      </div>
      <!-- 第二行：控制按钮 -->
      <div class="controls-row">
        <div class="left-controls">
          <div class="play-pause-btn control-item-base">
            <img @click="togglePlayPause" :src="playPauseIcon" alt="" />
          </div>
          <div class="volume-wrap control-item-base control-item-volume">
            <Volume
              ref="volume"
              :value="volume"
              :isLandscape="isLandscape"
              @input="changeVolume"
              :maxVolume="maxVolume"
            ></Volume>
          </div>
        </div>
        <div class="right-controls">
          <div
            v-if="showVideoNoteActions"
            class="operate-item operate-item__screenshot control-item-base"
          >
            <img
              @click="
                videoScreenShot({
                  type: 'screenshot',
                  title: '截图',
                })
              "
              src="../../../../../assets/images/reply/camera.png"
              alt=""
            />
          </div>
          <video-mark-popover
            v-if="showVideoNoteActions"
            ref="markPopover"
            class="operate-item control-item-base"
            @select="handleMarkSelect"
          />
          <div class="back-btn control-item-base">
            <img
              @click="handleBackFive"
              src="../../../../../assets/images/reply/back_five.png"
              alt=""
            />
          </div>
          <div class="forward-btn control-item-base">
            <img
              @click="handleForwardFive"
              src="../../../../../assets/images/reply/forward_five.png"
              alt=""
            />
          </div>
          <div v-if="isElectron" class="cut-btn control-item-base">
            <img
              @click="handleScreenshot"
              src="../../../../../assets/images/reply/cut.png"
              alt=""
            />
          </div>
          <Speed
            class="speed control-item-base"
            ref="speed"
            :value="playbackSpeed"
            :isLandscape="isLandscape"
            @change="changeSpeed"
          ></Speed>
          <!-- 字幕CC按钮 -->
          <div
            v-if="subtitleSwitch === 1"
            class="subtitle-cc-btn-wrap control-item-base"
            @click="handleCCClick"
          >
            <svg viewBox="0 0 24 24" width="30" height="30" :fill="subtitleCCColor">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM7.5 13h2c.28 0 .5-.22.5-.5V12h1.5v1c0 .83-.67 1.5-1.5 1.5h-2c-.83 0-1.5-.67-1.5-1.5v-3C6.5 9.17 7.17 8.5 8 8.5h2c.83 0 1.5.67 1.5 1.5v1H10v-.5c0-.28-.22-.5-.5-.5h-2c-.28 0-.5.22-.5.5v3c0 .28.22.5.5.5zm7 0h2c.28 0 .5-.22.5-.5V12h1.5v1c0 .83-.67 1.5-1.5 1.5h-2c-.83 0-1.5-.67-1.5-1.5v-3c0-.83.67-1.5 1.5-1.5h2c.83 0 1.5.67 1.5 1.5v1H17v-.5c0-.28-.22-.5-.5-.5h-2c-.28 0-.5.22-.5.5v3c0 .28.22.5.5.5z" />
            </svg>
          </div>
          <div class="full-screen-btn control-item-base">
            <img @click="toggleFullScreen" :src="screenIcon" alt="" />
          </div>
        </div>
      </div>
      <!-- 自定义标记标题弹窗 -->
      <custom-mark-title
        class="custom-mark-title"
        ref="customMarkTitle"
        @save="handleCustomMarkSave"
      />
    </div>
    <!-- 录播弹题：挂载点/仿写范式=shopping-cart（自包含 :dplayer 组件，自管数据与生命周期）。
         v-if="dplayer && lessonId" 保证 InteractionLayer 渲染时 dplayer 已实例化
         （initDplay 在 mounted 里同步赋值 this.dplayer，见下方 methods）。 -->
    <interaction-layer
      v-if="dplayer && lessonId"
      :dplayer="dplayer"
      :lesson-id="lessonId"
      :video-index="videoIndex"
      :is-h5="isH5"
      :is-landscape="isLandscape"
      @active-change="onQuizActiveChange"
      @input-mode-change="$emit('quiz-input-mode-change', $event)"
    />
    <!-- 购物车 -->
    <shopping-cart
      :lessonId="lessonInfo.id"
      @toggleFullScreen="toggleFullScreen"
      @changeFullScreenStatus="changeFullScreenStatus"
      :dplayer="dplayer"
      :class="isH5 && !fullScreenStatus ? '' : 'replay-cart'"
      :fullScreenStatus="fullScreenStatus"
      :suppressed="quizActive"
    />

    <!-- 网络未连接 -->
    <div
      v-show="showNetworkError"
      id="network-error-mask"
      class="network-error-mask"
    >
      <div class="cont-wrap">
        <p class="tips">网络未连接，请检查网络设置</p>
        <div class="operate-wrap">
          <button class="test-btn" @click="networkTest">在线检测</button>
          <button class="refresh-btn" @click="refreshRetry">
            <i class="el-icon-refresh-right"></i>
            <span>刷新重试</span>
          </button>
        </div>
      </div>
    </div>

    <!-- loading -->
    <img
      v-if="videoLoading"
      class="video-loading-img"
      src="../../../../../assets/images/icon/video-loading.gif"
    />
  </div>
</template>

<script>
import { EventBus } from "@saas/components/src/utils/event-bus";
import CustomMarkTitle from "./CustomMarkTitle.vue";
import SubtitleOverlay from "./SubtitleOverlay.vue";
import InteractionLayer from "./InteractionLayer.vue";
import { convertBase64ToFile, videoMarkPopover } from "@saas/components";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import DraggableProgressBar from "./DraggableProgressBar.vue";
import Volume from "./Volume.vue";
import Speed from "./Speed.vue";
import {
  isH5,
  isHXApp,
  isMobile,
  ShoppingCart,
  ToolBar,
  getCdnUrl,
} from "@saas/components/src";
import {
  getSharedPlayerVolume,
  setSharedPlayerVolume,
} from "../../../utils/player-volume";
import { isAudioLessonType } from "@saas/components/src/utils/lesson-type";
import { hidePlayerControlRefs } from "./player-control-lifecycle";
export default {
  name: "CustomPlayer",
  components: {
    DraggableProgressBar,
    Volume,
    Speed,
    ShoppingCart,
    ToolBar,
    videoMarkPopover,
    CustomMarkTitle,
    SubtitleOverlay,
    InteractionLayer,
  },
  props: {
    noteComponentId: {
      type: [String, Number],
      default: null,
    },
    openAiMode: {
      type: Boolean,
      default: false,
    },
    lessonInfo: {
      type: Object,
      default: () => {},
    },
    // 开始播放时长
    startTime: {
      type: Number,
      default: 0,
    },
    // 跳转播放位置
    jumpPlayTime: {
      type: Number,
      default: 0,
    },
    title: {
      type: String,
      default: "",
    },
    orgName: {
      type: String,
      default: "",
    },
    // 展示返回按钮
    showBack: {
      type: Boolean,
      default: false,
    },
    fullScreenStatus: {
      type: Boolean,
      default: false,
    },
    isShowTop: {
      type: Boolean,
      default: true,
    },
    isComponent: {
      type: Boolean,
      default: false,
    },
    urlProgressTime: {
      type: Number,
      default: null,
    }, //页面路径参数:播放位置
    subtitleUrl: {
      type: String,
      default: "",
    },
    subtitleSwitch: {
      type: Number,
      default: 2,
    },
    // 录播弹题：课时 ID，类型与 replay/Index.vue 的 data.lessonId 一致（路由参数是
    // String，componentParams 场景可能是 Number）——不用 lessonInfo.id（那个是
    // Number(lessonId) 转换过的，弹题接口走原始类型即可）。
    lessonId: {
      type: [String, Number],
      default: null,
    },
    // 录播弹题：当前段，0 基，= replay/Index.vue 的 currentIndex，透传给 InteractionLayer
    // 做按段过滤/切段重建 cue。
    videoIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      videoLoading: false,
      showNetworkError: false,
      loadingTimeout: null,
      LOADING_TIMEOUT: 15 * 1000,
      playIcon: require("../../../../../assets/images/reply/play.png"),
      pauseIcon: require("../../../../../assets/images/reply/pause.png"),
      fullScreenIcon: require("../../../../../assets/images/reply/full_screen.png"),
      exitFullScreenIcon: require("../../../../../assets/images/reply/exit_full_screen.png"),
      onTopIcon: require("../../../../../assets/images/reply/top.png"),
      noOnTopIcon: require("../../../../../assets/images/reply/no_top.png"),
      dplayer: null,
      isPlaying: false,
      // isFullScreen: false,
      // 播放进度
      currentProgress: 0,
      // 音量
      volume: 0,
      // 播放速度
      playbackSpeed: 1,
      // 当前时长
      currentTime: 0,
      // 总时长
      duration: 0,
      // 控制栏显示隐藏
      isShowControls: true,
      // 操作倒计时
      activityTimer: null,
      // 从头学展示倒计时
      replyTimer: null,
      // 是否展示从头学
      isShowReply: false,
      isH5: isMobile(),
      // 是否截图中
      isScreenshot: false,
      screenShot: null,
      // 是否置顶
      isOnTop: false,
      // 最大音量
      maxVolume: 100,
      isElectron: window.isElectron,
      // 视频是否加载完
      isLoaded: false,
      notice: "",
      noticeTimer: null,
      isShowPlayPauseIcon: false,
      isShowPlayPauseIconTimer: null,
      // 音频信息
      audioInfo: {
        context: null,
        source: null,
        gain: null,
        media: null,
        amplify: (multiplier) => {
          this.audioInfo.gain.gain.value = multiplier;
        },
        getAmpLevel: () => {
          return this.audioInfo.gain.gain.value;
        },
      },
      // 是否是新的视频
      isNewVideo: true,
      isLandscape:
        isMobile() && window?.screen?.orientation?.type?.includes("landscape"),
      isWin: false,
      faviconUrl: getCdnUrl("/cdn/static/images/favicon.ico"),
      subtitleActive: true,
      diagnosisTimeline: [],
      diagnosisLastProgressAt: 0,
      diagnosisLastVideoUrl: "",
      diagnosisPlaySessionId: `PLAY_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`,
      // 录播弹题：InteractionLayer 弹题态起落，用于抑制键盘热键与购物车显示
      quizActive: false,
    };
  },
  watch: {
    startTime: {
      handler(newValue) {
        if (newValue && !this.urlProgressTime) {
          this.isShowReply = true;
        } else {
          this.isShowReply = false;
        }
        if (this.dplayer) {
          // 优先页面路径time参数
          if (newValue && !this.urlProgressTime) {
            this.dplayer.seek(newValue);
          } else {
            this.dplayer.seek(0);
          }
        }
      },
      immediate: true,
    },
    jumpPlayTime: {
      handler(newValue) {
        this.isShowReply = false;
        if (this.dplayer) {
          if (newValue) {
            this.dplayer.seek(newValue);
          } else {
            this.dplayer.seek(0);
          }
          this.dplayer.play();
        }
      },
      immediate: true,
    },
    urlProgressTime: {
      handler(newValue) {
        this.isShowReply = false;
        if (this.dplayer) {
          if (newValue) {
            this.dplayer.seek(newValue);
          }
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters(["user"]),
    curNoteComponentId() {
      if (this.openAiMode) {
        return `ai-mode-note`;
      } else if (this.fullScreenStatus && !window.isElectron) {
        return `web-fullscreen-note`;
      } else {
        return this.noteComponentId;
      }
    },
    videoPlayPauseIcon() {
      return this.isPlaying ? this.playIcon : this.pauseIcon;
    },
    playPauseIcon() {
      return this.isPlaying ? this.playIcon : this.pauseIcon;
    },
    screenIcon() {
      return this.fullScreenStatus
        ? this.exitFullScreenIcon
        : this.fullScreenIcon;
    },
    topIcon() {
      return this.isOnTop ? this.onTopIcon : this.noOnTopIcon;
    },
    // 是否访录频
    isVisual() {
      return this.lessonInfo.risk_control_switch === 1;
    },
    isAudioLesson() {
      return isAudioLessonType(this.lessonInfo.lesson_type);
    },
    showVideoNoteActions() {
      return !this.isH5 && !this.isAudioLesson;
    },
    coverUrl() {
      return this.lessonInfo.cover_url || "";
    },
    audioVisualizerActive() {
      return this.isPlaying;
    },
    subtitleCCColor() {
      return this.subtitleActive ? '#13ce66' : 'rgba(255,255,255,0.6)';
    },
  },
  mounted() {
    this.initDplay();
    this.startListening();
    this.initStyle();
    if (window.isElectron && window.isWin) {
      this.isWin = window?.isWin();
    }
    EventBus.$off(`notice-video-screen-shot`);
    EventBus.$on(`notice-video-screen-shot`, this.handleNoticeVideoScreenShot);
    EventBus.$off(`notice-insert-mark-block`);
    EventBus.$on(`notice-insert-mark-block`, this.handleNoticeInsertMarkBlock);
    EventBus.$off(`notice-jump-play-time`);
    EventBus.$on(`notice-jump-play-time`, this.handleJumpPlayTime);
    window.addEventListener("online", this.handleDiagnosisNetworkChange);
    window.addEventListener("offline", this.handleDiagnosisNetworkChange);
  },
  beforeDestroy() {
    const dom = this.$refs.dplayerContainer;
    document.removeEventListener("mousemove", this.onActivety);
    document.removeEventListener("touchstart", this.onActivety);
    document.removeEventListener("touchmove", this.onActivety);
    document.removeEventListener("keydown", this.onkeydown);
    document.removeEventListener("click", this.onActivety);
    window.removeEventListener("orientationchange", this.onorientationchange);
    window.removeEventListener("online", this.handleDiagnosisNetworkChange);
    window.removeEventListener("offline", this.handleDiagnosisNetworkChange);
    clearTimeout(this.activityTimer);
    this.dplayer?.destroy();
  },
  methods: {
    ...mapMutations("learningRecord", ["setLastLesson", "delLastLesson"]),
    handleDbclick() {
      console.log("handleDbclick--windows");
      window.ReplyElectronApi.maxSizeWin();
    },
    closeWin() {
      EventBus.$emit("closeReplyWinByToolBar");
    },
    handleNoticeVideoScreenShot() {
      if (!this.showVideoNoteActions) return;
      console.log("====handleNoticeVideoScreenShot");

      this.videoScreenShot({
        type: "screenshot",
        title: "截图",
      });
    },
    handleNoticeInsertMarkBlock(data) {
      if (!this.showVideoNoteActions) return;
      console.log("data=====", data);

      this.videoScreenShot({
        ...data,
        title: data.content,
      });
    },
    handleJumpPlayTime(time) {
      this.isShowReply = false;
      if (this.dplayer) {
        if (time) {
          this.dplayer.seek(time);
        } else {
          this.dplayer.seek(0);
        }
        if (this.dplayer.video.duration > time) {
          this.dplayer.play();
        }
      }
    },
    handleCustomMarkSave(title) {
      if (!this.showVideoNoteActions) return;
      this.videoScreenShot({
        type: "custom",
        title,
        id: 1,
      });
    },
    handleMarkSelect(data) {
      if (!this.showVideoNoteActions) return;
      if (data.type === "custom") {
        this.$refs.customMarkTitle.handleShow();
      } else {
        this.videoScreenShot(data);
      }
    },
    // 视频截图
    videoScreenShot({ type, title, id }) {
      if (!this.showVideoNoteActions) return;
      const video = document.querySelector(
        ".dplayer-video.dplayer-video-current"
      );
      if (!video || !video.src) {
        this.$showToast(
          `${type === "screenshot" ? "截图" : "标记"}失败，请重新尝试`
        );
      } else {
        const cvs = document.createElement("canvas");
        const ctx = cvs.getContext("2d");
        cvs.width = video.videoWidth;
        cvs.height = video.videoHeight;
        let time = this.currentTime;
        ctx.drawImage(video, 0, 0, cvs.width, cvs.height);
        if (this.$refs?.progressBar?.currentTime) {
          time = this.$refs.progressBar.currentTime;
        }
        time = Math.floor(time);
        const dataURL = cvs.toDataURL("image/png");
        const file = convertBase64ToFile(dataURL, `screen-shot-${time}s.png`);
        this.$showToast(`${type === "screenshot" ? "截图" : "标记"}成功`);
        if (type === "custom" && this.$refs?.customMarkTitle) {
          this.$refs.customMarkTitle.handleClose();
        }
        console.log("noteComponentId", this.curNoteComponentId);
        const data = {
          file,
          time,
          title,
          type,
          id,
          noteComponentId: this.curNoteComponentId,
        };
        if (this.curNoteComponentId === "video-play-note") {
          EventBus.$emit("video-play-page-change-tab-to-note", data);
        } else {
          if (data.type === "screenshot") {
            EventBus.$emit(
              `handle-insert-video-screen-shot-${data.noteComponentId}`,
              {
                ...data,
                url: "",
              }
            );
            if (this.curNoteComponentId === "web-fullscreen-note") {
              EventBus.$emit(
                `handle-insert-video-screen-shot-video-play-note`,
                {
                  ...data,
                  url: "",
                }
              );
            }
          } else {
            const { id, time, title, file, type } = data;
            EventBus.$emit(`handle-insert-mark-block-${data.noteComponentId}`, {
              type,
              time,
              content: title,
              file,
              imgUrl: "",
            });
            if (this.curNoteComponentId === "web-fullscreen-note") {
              EventBus.$emit(`handle-insert-mark-block-video-play-note`, {
                type,
                time,
                content: title,
                file,
                imgUrl: "",
              });
            }
          }
        }
      }
    },
    refreshRetry() {
      this.showNetworkError = false;
      this.dplayer.play();
    },
    showLoadingMask() {
      this.hideLoadingGif();
      this.dplayer.pause();
      this.showNetworkError = true;
    },
    getDiagnosisBufferedEnd() {
      const video = this.dplayer?.video;
      if (!video?.buffered?.length) return 0;
      try {
        return video.buffered.end(video.buffered.length - 1);
      } catch (error) {
        return 0;
      }
    },
    recordDiagnosisEvent(name, extra = {}) {
      const now = Date.now();
      if (name === "progress" && now - this.diagnosisLastProgressAt < 2000) {
        return;
      }
      if (name === "progress") this.diagnosisLastProgressAt = now;
      this.diagnosisTimeline.push({
        name,
        at: now,
        current_time: this.getSafeCurrentTime(),
        buffered_end: this.getDiagnosisBufferedEnd(),
        ...extra,
      });
      this.diagnosisTimeline = this.diagnosisTimeline
        .filter((item) => now - item.at <= 30000)
        .slice(-100);
    },
    handleDiagnosisNetworkChange() {
      this.recordDiagnosisEvent("network_changed", {
        online: window.navigator.onLine,
      });
    },
    captureDiagnosisPlaybackSnapshot() {
      const mediaError = this.dplayer?.video?.error;
      return {
        state: this.showNetworkError
          ? "error"
          : this.videoLoading
            ? "buffering"
            : this.isPlaying
              ? "playing"
              : "paused",
        current_time: this.getSafeCurrentTime(),
        duration: this.getSafeDuration(),
        buffered_end: this.getDiagnosisBufferedEnd(),
        playback_speed: this.playbackSpeed,
        quality: this.dplayer?.quality?.name || "",
        error_code: mediaError?.code || "",
        error_message: mediaError?.message || "",
        online: window.navigator.onLine,
      };
    },
    networkTest() {
      const actualVideoUrl =
        this.dplayer?.video?.currentSrc || this.dplayer?.video?.src || "";
      const playbackSnapshot = this.captureDiagnosisPlaybackSnapshot();
      const diagnosisTimeline = this.diagnosisTimeline.slice(-100);
      let targetHost = "";
      try {
        targetHost = new URL(actualVideoUrl).hostname;
      } catch (error) {
        targetHost = "";
      }
      if (window?.ipcRenderer) {
        window.ipcRenderer.send("create-new-win", {
          key: "networkTest",
          url: `/network-test?lessonId=${this.lessonInfo.id}&source=video`,
          data: {
            userId: this.user?.user_id,
            orgId: this.user?.org_id,
            lessonId: this.lessonInfo.id,
            courseId: this.lessonInfo.course_id,
            videoId: this.lessonInfo.video_id || this.lessonInfo.resource_id,
            source: "video",
            actualVideoUrl,
            targetHost,
            playbackSnapshot,
            diagnosisTimeline,
            playSessionId: this.diagnosisPlaySessionId,
            route: this.$route?.fullPath || "",
          },
        });
      }
    },
    changeFullScreenStatus(status) {
      this.$emit("changeFullScreenStatus", status);
    },
    // 录播弹题：InteractionLayer 弹题态起落，抑制键盘热键（onkeydown 首行短路）与购物车
    // 显示（shopping-cart :suppressed），并通知外层在答题期间禁用课时切换。
    onQuizActiveChange(active) {
      this.quizActive = !!active;
      this.$emit("quiz-active-change", this.quizActive);
    },
    initStyle() {
      // 想把播放器自适应放在页面中间
      const documentRatio = window.innerWidth / window.innerHeight;
      const dplayRatio =
        this.$refs.dplayerContainer.offsetWidth /
        this.$refs.dplayerContainer.offsetHeight;
      if (documentRatio > dplayRatio) {
        this.$refs.dplayerContainer.style.height = "100%";
        this.$refs.dplayerContainer.style.width = "auto";
      } else {
        this.$refs.dplayerContainer.style.width = "100%";
        this.$refs.dplayerContainer.style.height = "auto";
      }
    },
    // 获取指定id设备的视频流
    getInitStream(source, audio) {
      return new Promise((resolve, _reject) => {
        // 获取指定窗口的媒体流
        // 此处遵循的是webRTC的接口类型  暂时TS类型没有支持  只能断言成any
        navigator.mediaDevices
          .getUserMedia({
            audio: audio
              ? {
                  mandatory: {
                    chromeMediaSource: "desktop",
                  },
                }
              : false,
            video: {
              mandatory: {
                chromeMediaSource: "desktop",
                chromeMediaSourceId: source.id,
              },
            },
          })
          .then((stream) => {
            resolve(stream);
          })
          .catch((error) => {
            console.log(error);
            resolve(null);
          });
      });
    },
    showLoadingGif() {
      this.videoLoading = true;
    },
    hideLoadingGif() {
      this.videoLoading = false;
    },
    initDplay() {
      if (typeof window.DPlayer !== "function") {
        return false;
      }
      if (window.isElectron) {
        this.showLoadingGif();
      }
      this.dplayer = new window.DPlayer({
        container: this.$refs.dplayerContainer,
        video: {},
        hotkey: false,
        screenshot: true,
      });
      const sharedVolume = getSharedPlayerVolume();
      this.volume = sharedVolume * this.maxVolume;
      this.dplayer.volume(sharedVolume);
      this.dplayer.on("error", () => {
        this.recordDiagnosisEvent("error", {
          error_code: this.dplayer?.video?.error?.code || "",
        });
        if (window.isElectron) {
          console.log("====error");
          this.showLoadingMask();
        }
      });
      this.dplayer.on("waiting", () => {
        this.recordDiagnosisEvent("waiting");
        if (window.isElectron) {
          console.log("=======waiting");
          this.showLoadingGif();
          this.loadingTimeout = setTimeout(() => {
            this.showLoadingMask();
          }, this.LOADING_TIMEOUT);
        }
      });
      // 监听播放事件
      this.dplayer.on("play", () => {
        this.recordDiagnosisEvent("play");
        this.dplayer.speed(this.playbackSpeed);
        this.isPlaying = true;
        this.isShowPlayPauseIcon = true;
        clearTimeout(this.isShowPlayPauseIconTimer);
        this.isShowPlayPauseIconTimer = setTimeout(() => {
          this.isShowPlayPauseIcon = false;
        }, 500);

        if (this.isPlaying && this.user?.user_id) {
          console.log("setLastLesson====LastLesson");
          // 记录为上次学到的课时
          let { course_id, unit_id, id } = this.lessonInfo;
          this.setLastLesson({
            user_id: this.user.user_id,
            ...this.lessonInfo,
            lesson_id: id,
          });
        }

        // if (this.isNewVideo) {
        //   this.isNewVideo = false;
        //   this.amplifyMedia(this.dplayer.video, 10);
        // }
        // this.volume = this.dplayer.video.volume * 100;
      });
      // 监听播放时间更新事件
      this.dplayer.on("timeupdate", () => {
        if (this.loadingTimeout) {
          this.hideLoadingGif();
          clearTimeout(this.loadingTimeout);
        }
        const currentTime = this.getSafeCurrentTime();
        const duration = this.getSafeDuration();
        this.currentTime = currentTime;
        this.duration = duration;
        this.currentProgress = duration ? currentTime / duration : 0;
      });
      // 监听视频时长更新事件
      this.dplayer.on("durationchange", () => {
        const currentTime = this.getSafeCurrentTime();
        const duration = this.getSafeDuration();
        this.currentTime = currentTime;
        this.duration = duration;
        this.$emit("videoDurationChange", this.duration);
        this.currentProgress = duration ? currentTime / duration : 0;
      });
      this.dplayer.on("loadeddata", () => {
        const currentUrl =
          this.dplayer?.video?.currentSrc || this.dplayer?.video?.src || "";
        if (currentUrl && currentUrl !== this.diagnosisLastVideoUrl) {
          this.diagnosisLastVideoUrl = currentUrl;
          this.diagnosisPlaySessionId = `PLAY_${Date.now()}_${Math.random()
            .toString(36)
            .slice(2, 8)}`;
          this.recordDiagnosisEvent("video_url_changed");
        }
        this.onActivety();
        this.isLoaded = true;
        if (window.isElectron) {
          this.hideLoadingGif();
          this.dplayer.play();
        }
        // 初始化视频播放时间： 优先页面路径time参数
        if (this.urlProgressTime || this.startTime) {
          let time = this.urlProgressTime || this.startTime;
          this.dplayer.seek(time);
          clearTimeout(this.replyTimer);
          this.replyTimer = setTimeout(() => {
            this.isShowReply = false;
          }, 3000);
        }
      });
      // 监听进度条拖动事件
      this.dplayer.on("progress", () => {
        this.recordDiagnosisEvent("progress");
        // console.log("progress");
        // console.log(this.dplayer);
      });
      // 监听播放速度变化事件
      this.dplayer.on("ratechange", () => {
        // console.log("ratechange");
      });
      this.dplayer.on("pause", () => {
        this.recordDiagnosisEvent("pause");
        // console.log("pause");
        this.isPlaying = false;
        this.isShowPlayPauseIcon = true;
        clearTimeout(this.isShowPlayPauseIconTimer);
        this.isShowPlayPauseIconTimer = setTimeout(() => {
          this.isShowPlayPauseIcon = false;
        }, 500);

        if (
          this.user?.user_id &&
          this.duration === (this.currentTime || this.dplayer.video.currentTime)
        ) {
          console.log("delLastLesson====LastLesson");
          // 移除上次学习课时
          this.delLastLesson({
            user_id: this.user.user_id,
            course_id: this.lessonInfo.course_id,
            lesson_id: this.lessonInfo.id,
          });
        }
      });
      this.dplayer.on("seeked", () => {
        this.recordDiagnosisEvent("seeked");
        // console.log("seeked");
      });
      this.dplayer.on("seeking", () => {
        this.recordDiagnosisEvent("seeking");
        // console.log("seeking");
      });
      this.dplayer.on("stalled", () => {
        this.recordDiagnosisEvent("stalled");
        // console.log("stalled");
      });
      this.dplayer.on("playing", () => {
        this.recordDiagnosisEvent("playing");
      });
      // this.dplayer.on("webfullscreen", () => {
      //   this.isFullScreen = true;
      // });
      // this.dplayer.on("webfullscreen_cancel", () => {
      //   this.isFullScreen = false;
      // });
      this.dplayer.on("resize", () => {
        // console.log(this.$refs.dplayerContainer);
        // console.log("resize");
        // console.log(this.dplayer);
      });
      this.dplayer.on("notice_show", (val) => {
        if (val.startsWith("快进") || val.startsWith("快退")) {
          this.notice = val;
          clearTimeout(this.noticeTimer);
          this.noticeTimer = setTimeout(() => {
            this.notice = "";
          }, 2000);
        }
      });
      return true;
    },
    // handleFullScreenChange() {
    //   if (
    //     document.fullscreenElement ||
    //     document.webkitFullscreenElement ||
    //     document.mozFullScreenElement ||
    //     document.msFullscreenElement
    //   ) {
    //     this.isFullScreen = true;
    //   } else {
    //     this.isFullScreen = false;
    //   }
    //   // if (this.isH5 && !this.isLandscape) {
    //   //   // 设置横屏样式
    //   //   document.documentElement.style.transform = "rotate(-90deg)";
    //   //   document.documentElement.style.width = "100vh";
    //   //   document.documentElement.style.height = "100vw";
    //   //   document.documentElement.style.position = "absolute";
    //   //   document.documentElement.style.top = "-100vh";
    //   //   document.documentElement.style.left = "0";
    //   // }
    // },
    // 监听dom元素的所有的操作事件
    startListening() {
      const dom = this.$refs.dplayerContainer;
      document.addEventListener("mousemove", this.onActivety);
      document.addEventListener("touchstart", this.onActivety);
      document.addEventListener("touchmove", this.onActivety);
      document.addEventListener("keydown", this.onkeydown);
      document.addEventListener("click", this.onActivety);
      // document.addEventListener(
      //   "fullscreenchange",
      //   this.handleFullScreenChange
      // );
      // document.addEventListener(
      //   "webkitfullscreenchange",
      //   this.handleFullScreenChange
      // );
      // document.addEventListener(
      //   "mozfullscreenchange",
      //   this.handleFullScreenChange
      // );
      // document.addEventListener(
      //   "MSFullscreenChange",
      //   this.handleFullScreenChange
      // );
      // 监听横竖屏切换
      window.addEventListener("orientationchange", this.onorientationchange);
    },
    onorientationchange() {
      this.isLandscape =
        this.isH5 && window.screen.orientation.type.includes("landscape");
    },
    onkeydown(e) {
      // 弹题态键盘归弹层：空格/箭头等热键在弹题卡片显示期间全部吞掉，避免绕过/误触播放
      if (this.quizActive) return;
      if (e.keyCode === 32) {
        this.dplayer.toggle();
      }
      if (e.keyCode === 37) {
        this.dplayer.seek(this.dplayer.video.currentTime - 5);
      } else if (e.keyCode === 39) {
        this.dplayer.seek(this.dplayer.video.currentTime + 5);
      }
      this.onActivety();
    },
    onActivety() {
      this.isShowControls = true;
      clearTimeout(this.activityTimer);
      // if (this.isPlaying) {
      //   this.activityTimer = setTimeout(() => {
      //     this.$refs.volume.isShow = false;
      //     this.$refs.speed.isShow = false;
      //     this.isShowControls = false;
      //   }, 5000);
      // }
      this.activityTimer = setTimeout(() => {
        // 自定义标记弹窗显示时-不自动隐藏控制栏
        if (hidePlayerControlRefs(this.$refs)) {
          this.isShowControls = false;
        }
      }, 5000);
    },
    // 从头学展示计时
    handleReplyTimer() {
      this.replyTimer = setTimeout(() => {
        this.isShowReply = false;
      }, 3000);
    },
    // 隐藏 展示 notice
    handleShowHideNotice(isShow) {
      const dom = this.$refs.dplayerContainer;
      if (dom) {
        const noticeDom = dom.querySelector(".dplayer-notice");
        if (noticeDom) {
          if (isShow) {
            noticeDom.style.display = "block";
          } else {
            noticeDom.style.display = "none";
          }
        }
      }
    },
    // 从头学
    handleReply() {
      // this.handleShowHideNotice(false);
      // this.dplayer.seek(0);
      // this.dplayer.play();
      this.$emit("afresh");
      this.isShowReply = false;
    },
    // CC按钮点击
    handleCCClick() {
      const overlay = this.$refs.subtitleOverlay;
      if (!overlay) return;
      // 没有字幕URL 或 字幕数据为空且不在加载中 → 字幕尚未生成
      if (!this.subtitleUrl || (!overlay.subtitleList.length && !overlay.loading)) {
        this.$message.info("字幕生成中，请稍后再试");
        return;
      }
      overlay.toggleSubtitle();
    },
    // 播放暂停
    togglePlayPause() {
      if (!this.dplayer) return;
      this.dplayer.toggle();
    },
    handleOnTop() {
      if (this.isElectron && window.getIsAlwaysOnTop) {
        window.getIsAlwaysOnTop().then((res) => {
          console.log(res);
          this.isOnTop = !res;
          window.toggleOnTop(!res);
        });
      }
    },
    // 截屏
    async handleScreenshot() {
      this.isScreenshot = true;
      const prePlayStatus = this.isPlaying;

      if (this.isPlaying) {
        this.dplayer.pause();
      }
      if (this.isElectron) {
        if (window.screenCaptureSwitch) {
          window.screenCaptureSwitch({
            isEnter: true,
            disableRecord: false,
          });
        }
        // 下面这两块自己考虑
        const sources = await window.getDesktopCapturerSelf(); // 这里返回的是设备上的所有窗口信息
        // 这里可以对`sources`数组下面id进行判断  找到当前的electron窗口  这里为了简单直接拿了第一个
        const stream = await this.getInitStream(sources);
        // eslint-disable-next-line no-undef
        new screenShotPlugin({
          enableWebRtc: true, // 启用webrtc
          screenFlow: stream, // 传入屏幕流数据
          level: 999999,
          toolPosition: "right",
          completeCallback: () => {
            console.log("截图完成");
            if (prePlayStatus) {
              this.dplayer.play();
            }
            this.isScreenshot = false;
            if (window.screenCaptureSwitch) {
              window.screenCaptureSwitch({
                isEnter: true,
                disableRecord: this.isVisual,
              });
            }
          },
          closeCallback: () => {
            console.log("截图取消");
            if (prePlayStatus) {
              this.dplayer.play();
            }
            this.isScreenshot = false;
            if (window.screenCaptureSwitch) {
              window.screenCaptureSwitch({
                isEnter: true,
                disableRecord: this.isVisual,
              });
            }
          },
        });
      } else {
        new window.screenShotPlugin({
          enableWebRtc: true,
          completeCallback: () => {
            console.log("截图完成");
            if (prePlayStatus) {
              this.dplayer.play();
            }
            this.isScreenshot = false;
          },
          closeCallback: () => {
            console.log("截图取消");
            if (prePlayStatus) {
              this.dplayer.play();
            }
            this.isScreenshot = false;
          },
        });
      }
    },
    getSafeDuration() {
      const videoDuration = Number(this.dplayer?.video?.duration);
      const stateDuration = Number(this.duration);
      if (Number.isFinite(videoDuration) && videoDuration > 0) {
        return videoDuration;
      }
      if (Number.isFinite(stateDuration) && stateDuration > 0) {
        return stateDuration;
      }
      return 0;
    },
    getSafeCurrentTime() {
      const videoCurrentTime = Number(this.dplayer?.video?.currentTime);
      const stateCurrentTime = Number(this.currentTime);
      if (Number.isFinite(videoCurrentTime) && videoCurrentTime >= 0) {
        return videoCurrentTime;
      }
      if (Number.isFinite(stateCurrentTime) && stateCurrentTime >= 0) {
        return stateCurrentTime;
      }
      return 0;
    },
    updateProgressByTime(currentTime, duration) {
      this.currentTime = currentTime;
      this.currentProgress = duration ? currentTime / duration : 0;
    },
    seekTo(val) {
      const duration = this.getSafeDuration();
      if (!duration || !this.dplayer) return;
      const progress = Math.max(0, Math.min(Number(val) || 0, 1));
      const seekTime = progress * duration;
      this.dplayer.seek(seekTime);
      this.updateProgressByTime(seekTime, duration);
    },
    playToSeekTime(seekTime) {
      this.dplayer.seek(seekTime);
    },
    seekBy(seconds) {
      if (!this.dplayer) return;
      const duration = this.getSafeDuration();
      const currentTime = this.getSafeCurrentTime();
      const target = Math.max(
        0,
        duration
          ? Math.min(currentTime + seconds, duration)
          : currentTime + seconds
      );
      this.dplayer.seek(target);
      this.updateProgressByTime(target, duration);
    },
    // 快退30秒
    handleBackFive() {
      this.seekBy(-30);
    },
    // 快进30秒
    handleForwardFive() {
      this.seekBy(30);
    },
    // 切换全屏
    toggleFullScreen() {
      this.$emit("toggleFullScreen");
      // this.isFullScreen = !this.isFullScreen;
      // if (this.isFullScreen) {
      //   this.dplayer.fullScreen.cancel("web");
      // } else {
      //   this.dplayer.fullScreen.request("web");
      // }
    },
    // 格式化时间 00:00:00 ，不够小时不显示
    formatTime(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);
      return `${hours < 10 ? "0" + hours : hours}:${
        minutes < 10 ? "0" + minutes : minutes
      }:${secs < 10 ? "0" + secs : secs}`;
    },
    // 改变音量
    changeVolume(val) {
      // this.handleShowHideNotice(false);
      const volume = val / this.maxVolume;
      setSharedPlayerVolume(volume);
      this.volume = volume * this.maxVolume;
      this.dplayer.volume(volume);
    },
    // 改变播放速度
    changeSpeed(val) {
      this.playbackSpeed = val;
      this.dplayer.speed(val);
    },
    destroyAudioProcessing() {
      console.log(this.audioInfo);
      if (this.audioInfo.source && this.audioInfo.gain) {
        this.audioInfo.source.disconnect();
        this.audioInfo.gain.disconnect();
        this.audioInfo.context.close();
        // this.audioInfo.source = null;
        // this.audioInfo.gain = null;
        // this.audioInfo.media = null;
      }
    },
    amplifyMedia(mediaElem, multiplier) {
      this.destroyAudioProcessing();
      var context =
        this.audioInfo.context ||
        new (window.AudioContext || window.webkitAudioContext)();
      this.audioInfo.context = context;
      this.audioInfo.source = context.createMediaElementSource(mediaElem);
      this.audioInfo.gain = context.createGain();
      this.audioInfo.media = mediaElem;
      this.audioInfo.source.connect(this.audioInfo.gain);
      this.audioInfo.gain.connect(context.destination);
      this.audioInfo.amplify(multiplier);
    },
  },
};
</script>

<style lang="scss" scoped>
.network-error-mask {
  --network-error-bg-image: url("../../../../../assets/images/video-play/network-bg.png");
  --network-error-bg-color: #edfff5;
  --network-error-text: #0d1400;
  --network-error-outline-text: #00c89d;
  --network-error-outline-border: #00e884;
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  .cont-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  width: 100%;
  height: 100%;
  background: var(--network-error-bg-image);
  background-size: 100% 100%;
  background-color: var(--network-error-bg-color);

  html.dark & {
    --network-error-bg-image: none;
    --network-error-bg-color: var(--mg-bg-surface);
    --network-error-text: var(--mg-text-primary);
    --network-error-outline-text: var(--mg-brand-primary);
    --network-error-outline-border: var(--mg-brand-primary);
  }

  .tips {
    font-weight: 600;
    font-size: 20px;
    color: var(--network-error-text);
    line-height: 28px;
    margin-bottom: 40px;
  }
  .operate-wrap {
    display: flex;
    align-items: center;
  }
  .test-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 16px;
    color: var(--network-error-outline-text);
    line-height: 26px;
    width: 96px;
    height: 40px;
    border-radius: 9px;
    margin-right: 19px;
    border: 1px solid var(--network-error-outline-border);
  }
  .refresh-btn {
    margin-top: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 16px;
    color: #ffffff;
    line-height: 26px;
    width: 120px;
    height: 40px;
    background: linear-gradient(132deg, #00eb81 0%, #00c89d 100%);
    i {
      font-size: 18px;
      color: #ffffff;
      margin-right: 8px;
    }
    border-radius: 9px;
  }
}
.custom-player-container {
  --video-note-action-icon-filter: brightness(0) invert(1) opacity(0.86);

  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
  .video-loading-img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    z-index: 999999999999;
  }
  .custom-mark-title {
    position: absolute;
    right: 65px;
  }
  .replay-cart {
    position: absolute;
    right: 60px;
    bottom: 200px;
    z-index: 9999;
  }
  &.custom-player-container__electron {
    display: flex;
    height: 100%;
    justify-content: center;
  }

  .dplayer-wrapper ::v-deep {
    width: 100%;
    height: auto;
    flex: 1;
    .dplayer-controller {
      display: none;
    }
    .dplayer-notice {
      display: none;
      left: 64px;
      z-index: 99999;
      bottom: 127px;
    }
    .dplayer-bezel-icon {
      display: none;
    }
    .dplayer-menu {
      display: none;
    }
    .dplayer-mobile-play {
      z-index: 9999999999;
    }
  }
  .dplayer-wrapper__full ::v-deep {
    // 为了解决在有些ios上全拼后会遮挡其他元素的问题
    video {
      position: fixed;
      z-index: 1;
    }
  }
  .dplayer-wrapper__show__control ::v-deep {
    .dplayer-mobile-play {
      z-index: 9999999999;
      display: block !important;
      svg {
        display: none !important;
      }
    }
  }
  .play-header {
    position: absolute;
    height: 150px;
    width: 100%;
    z-index: 1;
    left: 0;
    top: -200px;
    background: linear-gradient(
      0,
      rgba(13, 15, 20, 0) 0%,
      rgba(13, 15, 20, 0.9) 100%
    );
    transition: top ease-in-out 0.5s;
    .back-wrap {
      position: absolute;
      left: 62px;
      top: 60px;
      z-index: 1000;
      .back-btn {
        font-size: 20px;
        cursor: pointer;
        color: rgba(42, 177, 138, 1);
        display: flex;
        align-items: center;
        flex-direction: row;
        justify-content: flex-start;
        img {
          width: 24px;
          height: 24px;
          margin-right: 6px;
        }
      }
    }
    .name {
      color: #fff;
      font-weight: 500;
      font-size: 20px;
      color: #ffffff;
      width: 100%;
      margin-top: 60px;
      padding: 0 180px;
      line-height: 28px;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .play-header__show {
    top: 0;
  }
  .play-header__full {
    position: fixed;
    z-index: 9999999;
  }
  .paly-pause-icon-box {
    position: absolute;
    display: none;
    z-index: 9999999;
  }
  .paly-pause-icon-box__show {
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    margin: -26px 0 0 -26px;
    height: 52px;
    width: 52px;
    padding: 12px;
    box-sizing: border-box;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    opacity: 0.8;
    pointer-events: none;
    animation: scale-hide 0.5s linear;
    img {
      width: 100%;
      height: 100%;
    }
  }

  .big-play-btn {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: #3a3a3a2b;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1000;
    transition: all 0.3s ease;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);

    svg {
      margin-left: 4px;
    }

    &:hover {
      background: #00c89d;
      transform: translate(-50%, -50%) scale(1.1);
      border-color: #00c89d;
    }
  }

  .audio-cover-wrap {
    position: absolute;
    inset: 0;
    z-index: 2;
    background: radial-gradient(
      circle at 50% 40%,
      #334155 0%,
      #111827 48%,
      #020617 100%
    );
    overflow: hidden;
    pointer-events: none;
  }

  .audio-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: saturate(0.92) brightness(0.78);
    transform: scale(1.01);
  }

  .audio-cover-template {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
        180deg,
        rgba(15, 23, 42, 0.12),
        rgba(15, 23, 42, 0.56)
      ),
      linear-gradient(
        90deg,
        rgba(0, 200, 157, 0.08),
        rgba(0, 200, 157, 0.16),
        rgba(0, 200, 157, 0.08)
      );
  }

  .audio-visual-core {
    position: relative;
    z-index: 1;
    width: 168px;
    height: 168px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .audio-pulse-ring {
    position: absolute;
    left: 18px;
    right: 18px;
    top: 50%;
    height: 1px;
    transform: translateY(-50%);
    border-radius: 999px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(226, 232, 240, 0.42),
      transparent
    );
    opacity: 0.72;
    animation: audioRingPulse 2.8s ease-in-out infinite;
    animation-play-state: paused;
  }

  .audio-wave-bars {
    position: relative;
    z-index: 1;
    height: 58px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .audio-wave-bars span {
    display: block;
    width: 5px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(180deg, #f8fafc 0%, #00eb81 52%, #00c89d 100%);
    box-shadow: 0 0 16px rgba(0, 200, 157, 0.34);
    transform-origin: center;
    animation: audioWaveBounce 1.08s ease-in-out infinite;
    animation-play-state: paused;
  }

  .audio-wave-bars span:nth-child(3n) {
    height: 28px;
  }

  .audio-wave-bars span:nth-child(4n) {
    height: 36px;
  }

  .audio-wave-bars span:nth-child(5n) {
    height: 24px;
  }

  .is-visualizing {
    .audio-pulse-ring,
    .audio-wave-bars span {
      animation-play-state: running;
    }
  }

  .logo-container {
    position: absolute;
    right: 16px;
    top: 16px;
    z-index: 1000;
    display: flex;
    flex-direction: row;
    align-items: center;
    max-width: min(44vw, 360px);
    min-height: 36px;
    padding: 6px 12px;
    border-radius: 18px;
    background: rgba(2, 6, 23, 0.52);
    border: 1px solid rgba(255, 255, 255, 0.14);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    .logo {
      width: 22px;
      height: 22px;
      flex: 0 0 auto;
      border-radius: 6px;
    }
    .divider {
      margin: 0 10px;
      width: 1px;
      height: 8px;
      background: #ffffff;
      box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.3);
      border-radius: 2px;
    }
    .org-name {
      min-width: 0;
      font-size: 18px;
      color: #fff;
      text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
      line-height: 25px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .logo-container__full {
    position: fixed;
    z-index: 9999999;
  }
  .top-gradient-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.8), transparent);
    padding: 20px 24px;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1001;
    pointer-events: none;

    .top-gradient-title {
      color: #fff;
      font-weight: 700;
      font-size: 16px;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin: 0;
    }
  }
  .top-gradient-overlay__full {
    position: fixed;
    z-index: 9999999;
  }
  .top-gradient-overlay__show {
    opacity: 1;
  }
  .notice-box {
    position: absolute;
    height: 30px;
    line-height: 30px;
    padding: 0 15px;
    border-radius: 5px;
    opacity: 0.8;
    color: #fff;
    left: 20px;
    z-index: 99999;
    bottom: 112px;
    background: rgba(28, 28, 28, 0.9);
  }
  .notice-box__full {
    position: fixed;
    z-index: 9999999;
  }
  .last-play-position {
    position: absolute;
    bottom: 112px;
    left: 20px;
    display: flex;
    flex-direction: row;
    height: 28px;
    opacity: 0;
    transition: opacity ease-in-out 0.3s;
    z-index: 1002;
    span {
      font-size: 16px;
      color: #ffffff;
      line-height: 28px;
    }
    .reply-btn {
      font-weight: 600;
      font-size: 16px;
      color: #00c89d;
      line-height: 28px;
      margin-left: 10px;
      cursor: pointer;
    }
  }
  .last-play-position__full {
    position: fixed;
    z-index: 9999999;
  }
  .last-play-position__show {
    opacity: 1;
  }
  .player-controls {
    width: 100%;
    height: 112px;
    position: absolute;
    bottom: -112px;
    left: 0;
    right: 0;
    padding: 48px 20px 14px;
    box-sizing: border-box;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.95),
      rgba(0, 0, 0, 0.5) 60%,
      transparent
    );
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: stretch;
    gap: 10px;
    transition: bottom ease-in-out 0.5s;
    z-index: 1001;
    // 隐藏 DraggableProgressBar 内部时间，使用外层时间
    ::v-deep .progress-bar {
      .current-time,
      .total-time {
        display: none;
      }
    }
    .progress-row {
      width: 100%;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 12px;
      min-height: 20px;
      .current-time,
      .total-time {
        width: 64px;
        flex: 0 0 64px;
        font-size: 14px;
        color: #ffffff;
        font-weight: 500;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
        text-align: center;
        text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
      }
      .progress-bar-wrap {
        flex: 1;
        min-width: 0;
      }
    }
    .controls-row {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      min-height: 32px;
    }
    .left-controls {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 12px;
      min-width: 0;
    }
    .right-controls {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 12px;
      justify-content: flex-end;
      min-width: 0;
      flex-wrap: nowrap;
    }
    .control-item-base {
      width: 32px;
      height: 32px;
      display: flex;
      flex: 0 0 32px;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      img {
        width: 22px;
        height: 22px;
        cursor: pointer;
        object-fit: contain;
        display: block;
      }
    }
    .operate-item__screenshot img {
      filter: var(--video-note-action-icon-filter, none);
    }
    .control-item-volume {
      width: auto;
      min-width: 32px;
      flex: 0 0 auto;
    }
    .play-pause-btn {
      img {
        width: 24px;
        height: 24px;
      }
    }
    .speed {
      width: auto;
      min-width: 44px;
      padding: 0 4px;
      flex-basis: auto;
    }
    .subtitle-cc-btn-wrap {
      cursor: pointer;
      svg {
        width: 26px;
        height: 26px;
      }
      &:hover svg {
        opacity: 0.8;
      }
    }
  }
  .player-controls__full {
    position: fixed;
    z-index: 9999999;
  }
  .player-controls__show {
    bottom: 0;
  }
}
.custom-player-container__h5 {
  .dplayer-wrapper {
    // z-index: -1;
  }
  .top-gradient-overlay {
    height: 1.6rem;
    padding: 0.27rem 0.32rem;
    .top-gradient-title {
      font-size: 0.3733rem;
    }
  }
  .notice-box {
    bottom: 92px;
    height: 24px;
    line-height: 24px;
    padding: 0 8px;
    border-radius: 4px;
    opacity: 0.8;
    color: #fff;
    left: 12px;
    z-index: 99999;
    font-size: 12px;
    background: rgba(28, 28, 28, 0.9);
  }
  .play-header {
    position: absolute;
    height: 2.6667rem;
    width: 100%;
    z-index: 1;
    left: 0;
    top: -200px;
    background: linear-gradient(
      0,
      rgba(13, 15, 20, 0) 0%,
      rgba(13, 15, 20, 0.9) 100%
    );
    transition: top ease-in-out 0.5s;
    .back-wrap {
      position: absolute;
      left: 62px;
      top: 60px;
      z-index: 1000;
      .back-btn {
        font-size: 20px;
        cursor: pointer;
        color: rgba(42, 177, 138, 1);
        display: flex;
        align-items: center;
        flex-direction: row;
        justify-content: flex-start;
        img {
          width: 24px;
          height: 24px;
          margin-right: 6px;
        }
      }
    }
    .name {
      color: #fff;
      font-weight: 500;
      font-size: 20px;
      color: #ffffff;
      width: 100%;
      margin-top: 60px;
      padding: 0 180px;
      line-height: 28px;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .logo-container {
    right: 0.3941rem;
    top: 0.3756rem;
    .logo {
      width: 0.4rem;
      height: 0.4rem;
    }
    .divider {
      margin: 0 0.1333rem;
      width: 1px;
      height: 0.2rem;
      background: #ffffff;
      box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.3);
      border-radius: 0.0533rem;
    }
    .org-name {
      font-size: 0.32rem;
      color: #ffffff;
      line-height: 0.56rem;
    }
  }
  .logo-container__full {
    position: fixed;
    z-index: 9999999;
  }
  .last-play-position {
    bottom: 92px;
    left: 12px;
    span {
      font-size: 12px;
      line-height: 20px;
    }
    .reply-btn {
      font-size: 12px;
      line-height: 20px;
      margin-left: 8px;
    }
  }
  .player-controls {
    height: 84px;
    bottom: -84px;
    padding: 34px 12px 10px;
    gap: 8px;
    .progress-row {
      gap: 8px;
      min-height: 16px;
      .current-time,
      .total-time {
        width: 46px;
        flex-basis: 46px;
        font-size: 11px;
      }
    }
    .controls-row {
      min-height: 24px;
    }
    .left-controls {
      gap: 8px;
    }
    .right-controls {
      gap: 8px;
    }
    .control-item-base {
      width: 24px;
      height: 24px;
      flex-basis: 24px;
      img {
        width: 18px;
        height: 18px;
      }
    }
    .control-item-volume {
      width: auto;
      min-width: 24px;
      flex-basis: auto;
    }
    .play-pause-btn {
      img {
        width: 20px;
        height: 20px;
      }
    }
    .speed {
      min-width: 38px;
      padding: 0 2px;
    }
    .subtitle-cc-btn-wrap svg {
      width: 22px;
      height: 22px;
    }
  }
  .player-controls__full {
    position: fixed;
    z-index: 9999999;
  }
  .player-controls__show {
    bottom: 0;
  }
}
.custom-player-container__landscape {
  .top-gradient-overlay {
    height: 1.6rem;
    padding: 0.27rem 0.32rem;
    .top-gradient-title {
      font-size: 0.3733rem;
    }
  }
  .notice-box {
    bottom: 80px;
    height: 22px;
    line-height: 22px;
    padding: 0 8px;
    border-radius: 4px;
    opacity: 0.8;
    color: #fff;
    left: 12px;
    z-index: 99999;
    font-size: 11px;
    background: rgba(28, 28, 28, 0.9);
  }
  .play-header {
    position: absolute;
    height: 2.6667rem;
    width: 100%;
    z-index: 1;
    left: 0;
    top: -200px;
    background: linear-gradient(
      0,
      rgba(13, 15, 20, 0) 0%,
      rgba(13, 15, 20, 0.9) 100%
    );
    transition: top ease-in-out 0.5s;
    .back-wrap {
      position: absolute;
      left: 62px;
      top: 60px;
      z-index: 1000;
      .back-btn {
        font-size: 20px;
        cursor: pointer;
        color: rgba(42, 177, 138, 1);
        display: flex;
        align-items: center;
        flex-direction: row;
        justify-content: flex-start;
        img {
          width: 24px;
          height: 24px;
          margin-right: 6px;
        }
      }
    }
    .name {
      color: #fff;
      font-weight: 500;
      font-size: 20px;
      color: #ffffff;
      width: 100%;
      margin-top: 60px;
      padding: 0 180px;
      line-height: 28px;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .logo-container {
    right: 0.3941rem;
    top: 0.3756rem;
    .logo {
      width: 0.25rem;
      height: 0.25rem;
    }
    .divider {
      margin: 0 0.1333rem;
      width: 1px;
      height: 0.2rem;
      background: #ffffff;
      box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.3);
      border-radius: 0.0533rem;
    }
    .org-name {
      font-size: 0.1875rem;
      color: #ffffff;
      line-height: 0.56rem;
    }
  }
  .logo-container__full {
    position: fixed;
    z-index: 9999999;
  }
  .last-play-position {
    bottom: 80px;
    left: 12px;
    span {
      font-size: 11px;
      line-height: 18px;
    }
    .reply-btn {
      font-size: 11px;
      line-height: 18px;
      margin-left: 6px;
    }
  }
  .player-controls {
    height: 72px;
    bottom: -72px;
    padding: 28px 12px 8px;
    gap: 6px;
    .progress-row {
      gap: 8px;
      min-height: 14px;
      .current-time,
      .total-time {
        width: 42px;
        flex-basis: 42px;
        font-size: 10px;
      }
    }
    .controls-row {
      min-height: 22px;
    }
    .left-controls {
      gap: 8px;
    }
    .right-controls {
      gap: 8px;
    }
    .control-item-base {
      width: 22px;
      height: 22px;
      flex-basis: 22px;
      img {
        width: 16px;
        height: 16px;
      }
    }
    .control-item-volume {
      width: auto;
      min-width: 22px;
      flex-basis: auto;
    }
    .play-pause-btn {
      img {
        width: 18px;
        height: 18px;
      }
    }
    .speed {
      min-width: 36px;
      padding: 0 2px;
    }
    .subtitle-cc-btn-wrap svg {
      width: 20px;
      height: 20px;
    }
  }
  .player-controls__full {
    position: fixed;
    z-index: 9999999;
  }
  .player-controls__show {
    bottom: 0;
  }
}
.custom-player-container__component {
  height: 100%;
  .dplayer-wrapper {
    height: 100% !important;
  }
}
// ========== PAD 适配（768px ~ 1140px）：放大播放器底部控件方便触摸点击 ==========
@media screen and (min-width: 768px) and (max-width: 1140px) {
  // --- 桌面浏览器窄窗口（非 H5 模式） ---
  .custom-player-container:not(.custom-player-container__h5):not(.custom-player-container__landscape) {
    .player-controls {
      height: 144px;
      padding: 62px 36px 18px;
      bottom: -144px;
      gap: 14px;

      .progress-row {
        gap: 16px;
        .current-time,
        .total-time {
          width: 72px;
          flex-basis: 72px;
          font-size: 15px;
        }
      }

      .controls-row {
        min-height: 40px;
      }

      .left-controls,
      .right-controls {
        gap: 16px;
      }

      .control-item-base {
        width: 40px;
        height: 40px;
        flex-basis: 40px;
        img {
          width: 28px;
          height: 28px;
        }
      }

      .control-item-volume {
        width: auto;
        min-width: 40px;
        flex-basis: auto;
      }

      .play-pause-btn img {
        width: 30px;
        height: 30px;
      }

      .speed {
        min-width: 52px;
      }

      .subtitle-cc-btn-wrap {
        svg {
          width: 34px;
          height: 34px;
        }
      }
    }
    .player-controls__show {
      bottom: 0;
    }

    .paly-pause-icon-box__show {
      height: 60px;
      width: 60px;
      margin: -30px 0 0 -30px;
      padding: 14px;
    }

    .notice-box {
      bottom: 144px;
    }

    .last-play-position {
      bottom: 144px;
    }
  }
}

// ========== PAD 及大屏 H5 变体适配（包含横向/竖向模式，覆盖 rem 为 px） ==========
// min-height: 500px 用于排除手机的横屏模式 (手机横屏 height 通常 < 450px)
@media screen and (min-width: 768px) and (min-height: 500px) {
  .custom-player-container__h5,
  .custom-player-container__landscape {
    .player-controls {
      height: 128px !important;
      padding: 54px 32px 16px !important;
      bottom: -128px !important;
      gap: 12px !important;

      .progress-row {
        gap: 14px !important;
        min-height: 20px !important;
        .current-time,
        .total-time {
          width: 64px !important;
          flex-basis: 64px !important;
          font-size: 14px !important;
        }
      }

      .controls-row {
        min-height: 36px !important;
      }

      .left-controls,
      .right-controls {
        gap: 14px !important;
      }

      .control-item-base {
        width: 36px !important;
        height: 36px !important;
        flex-basis: 36px !important;
        img {
          width: 24px !important;
          height: 24px !important;
        }
      }

      .control-item-volume {
        width: auto !important;
        min-width: 36px !important;
        flex-basis: auto !important;
      }

      .play-pause-btn img {
        width: 26px !important;
        height: 26px !important;
      }

      .speed {
        min-width: 48px !important;
      }

      .subtitle-cc-btn-wrap {
        svg {
          width: 30px !important;
          height: 30px !important;
        }
      }
    }
    .player-controls__show {
      bottom: 0 !important;
    }

    .notice-box {
      bottom: 136px !important;
      height: 30px !important;
      line-height: 30px !important;
      font-size: 14px !important;
      left: 40px !important;
    }

    .last-play-position {
      bottom: 136px !important;
    }
  }
}
// 放大隐藏动画
@keyframes scale-hide {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

@keyframes audioWaveBounce {
  0%,
  100% {
    transform: scaleY(0.48);
    opacity: 0.72;
  }
  42% {
    transform: scaleY(1.34);
    opacity: 1;
  }
  68% {
    transform: scaleY(0.82);
    opacity: 0.86;
  }
}

@keyframes audioRingPulse {
  0%,
  100% {
    transform: translateY(-50%) scaleX(0.82);
    opacity: 0.42;
  }
  50% {
    transform: translateY(-50%) scaleX(1);
    opacity: 0.78;
  }
}

// electron
.custom-player-container__electron {
  ::v-deep .dplayer.dplayer-loading .dplayer-bezel .diplayer-loading-icon {
    display: none !important;
  }
  .top-gradient-overlay {
    display: none !important;
  }
}
.electron-play-header {
  -webkit-app-region: drag;
  position: absolute;
  height: 90px;
  width: 100%;
  z-index: 1003;
  left: 0;
  top: -200px;
  background: linear-gradient(
    0,
    rgba(13, 15, 20, 0) 0%,
    rgba(13, 15, 20, 0.9) 100%
  );
  transition: top ease-in-out 0.5s;

  ::v-deep .tool-bar {
    position: absolute;
    &.mac-bars {
      left: 15px;
      top: 30px;
    }
    &.win-bars {
      right: 20px;
      top: 30px;
    }
  }
  .name {
    width: 100%;
    margin-top: 26px;
    padding: 0 250px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    font-weight: 500;
    font-size: 20px;
    color: #ffffff;
    line-height: 28px;
  }

  .top-btn,
  .network-test-btn {
    -webkit-app-region: none;
    width: 30px;
    height: 30px;
    margin-right: 24px;
    img {
      width: 100%;
      height: 100%;
    }
  }
  .operate-wrap {
    position: absolute;
    right: 0;
    top: 25px;
    &.is-win {
      right: 120px;
    }
  }
}
.play-header__show {
  top: 0;
}
.play-header__full {
  position: fixed;
  z-index: 9999999;
}
// 响应式批次 2：≤1024px 控制栏适配
@media (max-width: 1024px) {
  .custom-player-container {
    .top-gradient-overlay .top-gradient-title {
      display: none;
    }
  }
  .custom-player-container:not(.custom-player-container__h5):not(.custom-player-container__landscape) {
    .player-controls {
      height: 96px;
      bottom: -96px;
      padding: 42px 12px 10px;
      gap: 8px;
      .progress-row {
        gap: 8px;
        min-height: 18px;
        .current-time,
        .total-time {
          width: 54px;
          flex-basis: 54px;
          font-size: 12px;
        }
      }
      .controls-row {
        min-height: 28px;
      }
      .left-controls {
        gap: 12px;
      }
      .right-controls {
        gap: 12px;
        flex-wrap: nowrap;
      }
      .control-item-base {
        width: 28px;
        height: 28px;
        flex-basis: 28px;
        img {
          width: 20px;
          height: 20px;
        }
      }
      .control-item-volume {
        width: auto;
        min-width: 28px;
        flex-basis: auto;
      }
      .play-pause-btn img {
        width: 22px;
        height: 22px;
      }
      .speed {
        min-width: 42px;
      }
      .subtitle-cc-btn-wrap svg {
        width: 24px;
        height: 24px;
      }
    }
  }
}
</style>
<style lang="scss">
.replay-landscape {
  .replay-cart {
    position: absolute;
    right: 60px;
    bottom: 130px;
    z-index: 2100;
  }
}
.page-container__full {
  .replay-cart {
    z-index: 99999999 !important;
  }
}
</style>
